<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { useTimeoutFn, watchDebounced } from '@vueuse/core'
import GTag from '@consumer/components/GTag/GTag.vue'
import type { SignalName } from './conciergeState'
import { useConciergeState } from './conciergeState'
import { parseSignals, type SignalParser } from './parseSignals'
import { budgetLabel } from './signalDictionary'
import { useTypewriterPlaceholder } from './useTypewriterPlaceholder'
import {
  ghostContinuation,
  nextSignalPrompt,
  recommendationReadiness,
} from './recommendationRequest'
import { trackConciergeEvent } from './conciergeEventTracking'

// The parser is injectable so an alternate implementation (a fuller word list,
// later an LLM) swaps in without touching this component. This component owns the
// input, live signal parsing, and the detected-signal chips; the results surface
// is mounted separately (the rich concierge island).
const props = defineProps<{
  parser?: SignalParser
}>()

const state = useConciergeState()
const parse = props.parser ?? parseSignals

const description = _ref(state.raw)

// TEMPORARY (concept-proving): hardcoded rotating placeholder examples. Final
// copy is a content decision, not these literals.
const EXAMPLES = [
  'a birthday gift for my sister in Seattle who loves sushi',
  "something for my coworker who's really into cooking",
  "a retirement gift for my dad who's into golf and jazz",
  'an anniversary present for my wife in New York',
]
const placeholder = (useTypewriterPlaceholder(EXAMPLES, () => !description.value.trim(), { holdMs: 5000 }))

const readiness = _computed(() => recommendationReadiness(state.signals))

let interacted = false
// The takeover teleport blurs then re-focuses this field on engage; dedupe so that restore
// doesn't log a second input_focus (re-engagement shows in takeover_engage).
let focusTracked = false
function onFocus () {
  if (focusTracked) return
  focusTracked = true
  trackConciergeEvent('input_focus', { variant: 'free_text' })
}
function onFirstKeystroke () {
  if (interacted) return
  interacted = true
  trackConciergeEvent('first_interaction', { variant: 'free_text' })
}

type DetectedChip = { key: string, label: string, emoji: string }

// TEMPORARY (concept-proving): interests use one placeholder glyph. In production
// each interest carries its own GiftTag icon_url — the endpoint already returns it.
const INTEREST_GLYPH = '✨'

// Chips echo the buyer's own wording where the parser kept it, so someone who
// typed "husband" isn't shown "spouse". The canonical still drives everything
// downstream; this is display only.
const detected = _computed<DetectedChip[]>(() => {
  const { relationship, occasion, location, interests, budget } = state.signals
  const chips: DetectedChip[] = []
  if (relationship.value)
    chips.push({ key: `r-${relationship.value}`, label: relationship.display ?? relationship.value, emoji: '🧑' })
  if (occasion.value)
    chips.push({ key: `o-${occasion.value}`, label: occasion.display ?? occasion.value, emoji: '🎉' })
  if (location.value)
    chips.push({ key: `l-${location.value}`, label: location.display ?? location.value, emoji: '📍' })
  for (const interest of interests.value)
    chips.push({ key: `i-${interest}`, label: interest, emoji: INTEREST_GLYPH })
  if (budget.value) chips.push({ key: 'budget', label: budgetLabel(budget.value), emoji: '💰' })
  return chips
})

// Ghost suggestion: a greyed completion nudging the next useful detail. It waits
// out a 2s typing pause, vanishes the instant a key is pressed, and only returns
// if a suggestion still makes sense. Cities and interests rotate so it never
// feels like it's forcing one answer.
const GHOST_DELAY_MS = 2000
// TEMPORARY (concept-proving): hardcoded example pools for the nudge. Real
// versions come from popular-cities and popular-interest data, not these lists.
const CITY_EXAMPLES = ['Seattle', 'Austin', 'Chicago', 'New York', 'Denver', 'Nashville', 'Portland']
const INTEREST_EXAMPLES = ['coffee', 'hiking', 'cooking', 'running', 'jazz', 'gaming', 'gardening']

let ghostText = _ref('')

function sample (options: string[]): string {
  return options[Math.floor(Math.random() * options.length)]
}

function ghostPhraseFor (field: SignalName): string {
  switch (field) {
    case 'location': return `in ${sample(CITY_EXAMPLES)}`
    case 'interests': return `who loves ${sample(INTEREST_EXAMPLES)}`
    case 'relationship': return 'for your sister'
    case 'occasion': return 'for their birthday'
    default: return ''
  }
}

function refreshGhost () {
  if (readiness.value.ready || readiness.value.filledCount < 1) return
  const text = description.value.trim()
  if (!text || text.length > 44) return
  const field = nextSignalPrompt(state.signals)
  if (field) ghostText.value = ghostContinuation(description.value, ghostPhraseFor(field))
}

const { start: scheduleGhost } = useTimeoutFn(refreshGhost, GHOST_DELAY_MS, { immediate: false })

watch(() => description.value, () => {
  ghostText.value = ''
  scheduleGhost()
})

const announcement = _computed(() => {
  if (!detected.value.length) return ''
  return readiness.value.ready
    ? `${readiness.value.filledCount} details detected. Ready to find gifts.`
    : readiness.value.reason
})

// Reacts as the buyer types: re-read the whole field and refresh the detected
// signals in the store (the results surface watches those signals and fetches).
// The seq guard drops a slow parse superseded by a newer keystroke (matters once
// an async parser is injected), so chips never show signals from text the buyer
// already edited away.
let parseSeq = 0
watchDebounced(
  () => description.value,
  async (raw) => {
    const seq = ++parseSeq
    state.raw = raw
    const { signals, surfaces } = await parse(raw)
    if (seq !== parseSeq) return
    state.replaceFromParse(signals, surfaces)
  },
  { debounce: 220, immediate: true },
)
</script>

<template>
  <div class="concierge-input">
    <label for="concierge-description">What are you shopping for?</label>
    <div class="input-wrap">
      <div class="input-inner">
        <input
          id="concierge-description"
          v-model="description"
          type="text"
          autocomplete="off"
          :placeholder="placeholder"
          @focus="onFocus"
          @input="onFirstKeystroke"
        >
        <div v-if="ghostText" class="ghost" aria-hidden="true">
          <span class="ghost-typed">{{ description }}</span><span class="ghost-hint">&nbsp;{{ ghostText }}…</span>
        </div>
      </div>
    </div>

    <TransitionGroup
      v-if="detected.length"
      tag="ul"
      name="chip"
      class="detected"
      aria-label="Detected details"
    >
      <li v-for="chip in detected" :key="chip.key">
        <GTag size="xs" variant="unselected" :emoji="chip.emoji" :label="chip.label"/>
      </li>
    </TransitionGroup>

    <!-- Only nudge for the next detail while there isn't enough yet; once ready,
         the results speak for themselves. -->
    <p v-if="detected.length && !readiness.ready" class="request-hint">{{ readiness.reason }}</p>

    <p class="sr-only" role="status" aria-live="polite">{{ announcement }}</p>
  </div>
</template>

<style lang="scss" scoped>
@use "./conciergeRainbow" as rainbow;

// Caps its own column: the takeover surface spans the page's content width for
// the results, so the input centers itself at a readable width within it.
.concierge-input {
  display: grid;
  gap: 8px;
  margin: 0 auto;
  max-width: 620px;
}

label {
  font-size: 18px;
  font-weight: 700;
  text-align: center;
}

// The rainbow lives on the wrapper as a 2px gradient frame; the white inner box
// masks it so only the border shows. It drifts slowly and warms up on focus.
.input-wrap {
  animation: concierge-rainbow-pan 8s linear infinite;
  background: rainbow.$gradient;
  background-size: 200% 100%;
  border-radius: 16px;
  padding: 2px;

  &:focus-within {
    animation-duration: 3s;
    filter: saturate(1.3);
  }
}

@media (prefers-reduced-motion: reduce) {
  .input-wrap {
    animation: none;
  }
}

.input-inner {
  background: white;
  border-radius: 14px;
  position: relative;
}

// Input and the ghost overlay share one text box so the greyed suggestion lines
// up right after what the buyer typed.
input,
.ghost {
  background: transparent;
  border: 0;
  box-sizing: border-box;
  font: inherit;
  font-size: 16px;
  letter-spacing: normal;
  line-height: 1.4;
  padding: 14px 16px;
  width: 100%;
}

input {
  border-radius: 14px;

  &::placeholder {
    color: var(--color-text-tertiary);
  }

  &:focus-visible {
    outline: none;
  }
}

// The typed copy is transparent — it only reserves width so the hint sits after it.
.ghost {
  color: transparent;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  white-space: pre;
}

.ghost-hint {
  color: var(--color-text-tertiary);
}

.detected {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
}

.chip-enter-active,
.chip-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.chip-enter-from,
.chip-leave-to {
  opacity: 0;
  transform: scale(0.85);
}

.request-hint {
  color: var(--color-text-tertiary);
  font-size: 13px;
  margin: 4px 0 0;
}
</style>
